import { createRootRoute, Outlet } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { useDispatch } from 'react-redux';
// Libs
import { reportError } from 'redux/actions/thunks/AppErrorThunk';
import AppConfig from 'appConfig';
import { getAppTitle, getModuleFromUrl, isZywaveUrl } from 'lib/AppHelpers';
import InternalError from 'components/InternalError/InternalError';
import { toBrowserPath } from 'lib/config/appConstants';
import { useUserInfo } from 'hooks/useUserInfo';
// TP Components
import Favicon from 'react-favicon';
import { Helmet } from 'react-helmet-async';
import ZuiHeader from 'components/Layout/ZuiHeader';
import ZuiNavigation from 'components/Layout/ZuiNavigation';
import ZuiFooter from 'components/Layout/ZuiFooter';

// Timeout for waiting for shell components (15 seconds)
const SHELL_TIMEOUT_MS = 15000;

// localStorage key for tracking shell load retry
const SHELL_RETRY_KEY = 'ZYWAVE-SHELL:RETRY_COUNT';

/**
 * Helper to create a timeout promise that rejects after a delay
 */
function createTimeout(ms: number, message: string): Promise<never> {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error(message)), ms);
  });
}

/**
 * Handle shell loading failure - retry once by refreshing, then show error
 */
function handleShellLoadFailure(): never {
  const retryCount = parseInt(localStorage.getItem(SHELL_RETRY_KEY) || '0', 10);

  if (retryCount < 1) {
    // First failure - increment retry count and refresh
    localStorage.setItem(SHELL_RETRY_KEY, String(retryCount + 1));
    window.location.reload();
    // Throw to prevent further execution while page reloads
    throw new Error('Refreshing page to retry shell load...');
  }

  // Already retried - clear the counter and throw error to show error page
  localStorage.removeItem(SHELL_RETRY_KEY);
  throw new Error('Zywave shell components failed to load after retry');
}

/**
 * Clear the shell retry counter on successful load
 */
function clearShellRetryCounter() {
  localStorage.removeItem(SHELL_RETRY_KEY);
}

/**
 * beforeLoad hook that waits for Zywave shell components to be defined.
 * This ensures child routes (like authGuard) can safely use the proxy.
 * Times out after 15 seconds if components fail to load.
 * Retries once by refreshing the page before showing an error.
 */
async function waitForShellComponents() {
  if (isZywaveUrl()) {
    try {
      // Wait for both shell and proxy components to be defined, with timeout
      // This ensures they're ready before any child route's beforeLoad runs
      await Promise.race([
        Promise.all([
          customElements.whenDefined('zywave-shell'),
          customElements.whenDefined('zywave-api-proxy'),
        ]),
        createTimeout(SHELL_TIMEOUT_MS, 'Zywave shell components failed to load'),
      ]);

      // Success - clear any retry counter
      clearShellRetryCounter();
    } catch {
      // Timeout occurred - handle with retry logic
      handleShellLoadFailure();
    }
  }
}

/**
 * Error component shown when shell components fail to load
 */
function RootErrorComponent() {
  return <InternalError statusCode="500" />;
}

export const Route = createRootRoute({
  beforeLoad: waitForShellComponents,
  component: RootComponent,
  errorComponent: RootErrorComponent,
});

/**
 * Root component for TanStack Router
 *
 * This component wraps all routes with the application layout (ZUI shell, header, navigation, footer).
 *
 * By the time this component renders, the shell components are guaranteed to be ready.
 */
function RootComponent() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();

  const { data: userInfo } = useUserInfo();

  const module = getModuleFromUrl(window.location.href) as 'EB' | 'PC' | undefined;

  // set the favicon and title based on module
  const favIconUrl = `${process.env.PUBLIC_URL}/zywave-favicon.ico`;
  const appTitle = getAppTitle({ module });

  // Error handler that integrates with Redux error handling system
  const onAppError = (error: Error) => {
    dispatch(reportError(error));
  };

  if (isZywaveUrl()) {
    return (
      <>
        <zywave-api-proxy api-base-url={AppConfig.zywaveProxyUrl}></zywave-api-proxy>
        <zywave-shell unresolved appName={appTitle} logout-user-url={toBrowserPath('/edge/logout')}>
          <zywave-shell-content unresolved>
            <Outlet />
          </zywave-shell-content>
        </zywave-shell>
        {process.env.NODE_ENV !== 'production' && (
          <TanStackRouterDevtools position="bottom-right" />
        )}
      </>
    );
  }

  return (
    <zui-shell unresolved>
      <Helmet>
        <title>{appTitle}</title>
      </Helmet>
      <Favicon url={favIconUrl} />
      <ZuiHeader module={module} />
      {(userInfo?.products?.length ?? 0) > 0 && (
        <ZuiNavigation onAppError={onAppError} userInfo={userInfo} module={module} />
      )}
      <zui-shell-content slot="content">
        <Outlet />
      </zui-shell-content>
      <ZuiFooter />
      {process.env.NODE_ENV !== 'production' && <TanStackRouterDevtools position="bottom-right" />}
    </zui-shell>
  );
}
